import BasePlayer from './base';

import events from '../events';

import { createElement, append, each, find, first, isArray, uidGenerator, findIndex } from '../utils';
import isIOS from '../helpers/isiOS';
import {
   UNTRANSLATED_TIMEDATA_STATUS
} from '../config/statuses';

export default class HtmlPlayer extends BasePlayer {
   $el;
   source;
   _levels = [];
   _currentLevel = null;

   constructor(options, state) {
      super(options, state);
      this.logger.debug('html:player:creating');

      let tag = 'video';
      const params = { id: uidGenerator(), tabindex: -1 };

      if ( options.disableAutoFullScreenOnIOS ) {
         params['webkit-playsinline'] = true;
         params.playsinline = true;
      }

      if ( options.isMobile ) {
         params.preload = 'metadata';
         // use audio for audio. fix for ios safari RAV-2462
         tag = options.isAudio ? 'audio' : 'video';
      }

      /** @type {HTMLMediaElement} */
      // use video also for audio. fix for opera. RAV-412
      this.$el = createElement(tag, params);

      each(events, type => {
         this.addEvent(this.$el, type, event => {
            this.logger.debug('triggered event', type);
            this.bus.emit(type, event);
         });
      });

      this.handleFullscreenChanging = this.handleFullscreenChanging.bind(this);
      this.handleWebkitEndFullscreen = this.handleWebkitEndFullscreen.bind(this);

      if (isIOS()) {
         this.bus.on('fullscreenchanging', this.handleFullscreenChanging);
      }
      this.logger.debug('html:player:created');
   }

   load({type, src, cc}) {
      let options = { type, src };
      // i hate opera, safari, iOS
      if (this.options.isMobile && type === 'audio/mp3') {
         options = { src };
      }
      if ( isArray(src) ) {
         this._levels = src;
         if (this.options.qualityLevel) {
            const h = parseInt(this.options.qualityLevel, 10);
            if (h) {
               this._currentLevel = find(this._levels, l => parseInt(l.name, 10) === h);
            }
         }
         this._currentLevel = this._currentLevel || find(this._levels, lvl => lvl.default);
         this._currentLevel = this._currentLevel || first(this._levels);
         options.src = this._currentLevel.src;
      }

      this.logger.debug('html:player:loading', src);
      this.source = createElement('source', options);

      this.source.addEventListener('error', (e) => {
         this.bus.emit('error', e);
         this.logger.error(e);
      });

      append(this.$el, this.source);

      if (isIOS()) {
         each(cc, (track) => {
            if (track.state !== UNTRANSLATED_TIMEDATA_STATUS && track.type !== 'json') {
               append(this.$el, createElement('track', track));
            }
         });
      }

      if (this.$el.audioTracks) {
         this.$el.audioTracks.onchange = () => {
            this.bus.emit('audiotrackswitched');
         };
      }

      this.$el.addEventListener('webkitendfullscreen', this.handleWebkitEndFullscreen);
      if (isIOS()) {
         this.bus.once('loadeddata', () => {
            const textTracks = this.getTextTracks();
            if (textTracks instanceof TextTrackList) {
               textTracks.addEventListener('change', () => {
                  const tt = find(textTracks, (tt) => tt.mode === 'showing');
                  if (tt) {
                     this.bus.emit('cclang', tt.language);
                  }
               });
            }
            this.handleWebkitEndFullscreen();
         });
      }
   }

   getLevels() {
      return this._levels;
   }

   setLevel(level) {
      const currentTime = this.$el.currentTime;
      const paused = this.$el.paused;
      this._currentLevel = level;
      this.$el.pause();
      this.source.src = level.src;

      this.bus.once('loadedmetadata', () => {
         this.$el.currentTime = currentTime;

         if (!paused) {
            this.$el.play();
         }

      });
      this.$el.currentTime = currentTime;
      this.$el.load();
   }

   setPlaybackRate(rate) {
      this.$el.playbackRate = rate;
      this.$el.defaultPlaybackRate = rate;
   }

   getLevel() {
      return this._currentLevel;
   }

   getElement() {
      return this.$el;
   }

   getBufferedPercent() {
      const target = this.$el;
      if (target && target.buffered && target.buffered.length > 0 && target.buffered.end && target.duration) {
         return target.buffered.end(target.buffered.length - 1) / target.duration;
      }

      return 0;
   }

   getAudioTrack() {
      return findIndex(this.getAudioTracks(), (track) => track.enabled);
   }

   getAudioTracks() {
      return this.$el.audioTracks || [];
   }

   setAudioTrack(idx) {
      each(this.getAudioTracks(), (track, index) => {
         if (track && typeof track === 'object') {
            track.enabled = index == idx;
         }
      });
   }

   getTextTracks() {
      return this.$el.textTracks || [];
   }

   handleFullscreenChanging(flag) {
      const lang = flag !== true ? this.state.getCCLang() : '__';
      this._textTrackWalker((track) => track.language === lang ? 'showing' : 'disabled');
   }

   handleWebkitEndFullscreen() {
      this.handleFullscreenChanging(this.$el.webkitDisplayingFullscreen);
   }

   _textTrackWalker(modeFiller) {
      each(this.getTextTracks(), (track) => {
         if (track && typeof track === 'object') {
            track.mode = modeFiller(track);
         }
      });
   }

   destroy() {
      this.$el.removeEventListener('webkitendfullscreen', this.handleWebkitEndFullscreen);
      this.bus.off('fullscreenchanging', this.handleFullscreenChanging);
      super.destroy();
      this.$el.parentNode.removeChild(this.$el);
      this.$el = null;
      this.source = null;
   }
}
